import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Trash2, Plus, ClipboardList, Eye, Link2, Copy, Check, XCircle, Calendar, TrendingUp } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'
import SavedIndicator from '../components/SavedIndicator'

const FIELDS = [
  ['name',                       'Name',                   'text'],
  ['contact_name',               'Contact name',           'text'],
  ['contact_email',              'Contact email',          'email'],
  ['contact_phone',              'Contact phone',          'tel'],
  ['delivery_address',           'Delivery address',       'textarea'],
  ['delivery_postcode',          'Delivery postcode',      'text'],
  ['billing_address',            'Billing address',        'textarea'],
  ['billing_postcode',           'Billing postcode',       'text'],
  ['sage_customer_account_number', 'Sage account #',       'text'],
  ['notes',                      'Internal notes',         'textarea'],
]
// client_group_id is rendered as a dropdown separately below.

export default function CompanyDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form, setForm] = useState(null)
  const [cards, setCards] = useState([])
  const [groups, setGroups] = useState([])
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  const [newCardName, setNewCardName] = useState('')
  const [previewLinks, setPreviewLinks] = useState([])
  const [creatingLink, setCreatingLink] = useState(false)
  const [copiedToken, setCopiedToken] = useState(null)
  const [newLinkLabel, setNewLinkLabel] = useState('')

  const load = async () => {
    if (isDemoMode) { setForm({}); return }
    const { data } = await supabase.from('companies').select('*').eq('id', id).maybeSingle()
    if (data) setForm(data)
    const { data: cs } = await supabase
      .from('cards')
      .select('id, name, notes')
      .eq('company_id', id)
      .is('archived_at', null)
      .order('name')
    setCards(cs || [])
    const { data: gs } = await supabase.from('client_groups').select('id, name').is('archived_at', null).order('name')
    setGroups(gs || [])
    const { data: pls } = await supabase.from('preview_links')
      .select('*').eq('company_id', id).order('created_at', { ascending: false })
    setPreviewLinks(pls || [])
  }

  const createPreviewLink = async () => {
    if (isDemoMode) return
    setCreatingLink(true)
    try {
      const { data, error } = await supabase.rpc('create_preview_link', {
        p_company_id: id,
        p_label: newLinkLabel || null,
        p_days: 30,
      })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Failed to create link')
      setNewLinkLabel('')
      await load()
    } catch (err) {
      alert(err.message || 'Could not create preview link')
    } finally {
      setCreatingLink(false)
    }
  }

  const revokePreviewLink = async (token) => {
    if (!confirm('Revoke this preview link? Anyone who has it will no longer be able to view.')) return
    await supabase.from('preview_links').update({ revoked_at: new Date().toISOString() }).eq('token', token)
    await load()
  }

  const copyPreviewUrl = async (token) => {
    const url = `${window.location.origin}/preview/${token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(t => (t === token ? null : t)), 2000)
    } catch {
      // Clipboard may be blocked; select-and-copy fallback via prompt
      window.prompt('Copy this URL:', url)
    }
  }
  useEffect(() => { load() }, [id])

  const save = async () => {
    if (isDemoMode) return
    setSaving(true)
    const { id: _, created_at, updated_at, ...payload } = form
    const { error } = await supabase.from('companies').update(payload).eq('id', id)
    setSaving(false)
    if (error) return alert(error.message)
    setSavedAt(Date.now())
  }

  const archive = async () => {
    if (isDemoMode) return
    if (!confirm('Archive this company? Orders are kept; the company is hidden from lists.')) return
    await supabase.from('companies').update({ archived_at: new Date().toISOString() }).eq('id', id)
    navigate('/companies')
  }

  const addCard = async (e) => {
    e.preventDefault()
    if (!newCardName.trim() || isDemoMode) return
    const { error } = await supabase.from('cards').insert({ company_id: id, name: newCardName.trim() })
    if (error) return alert(error.message)
    setNewCardName('')
    load()
  }

  if (!form) return <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>

  return (
    <div className="space-y-4">
      <DemoNotice />

      <Link to="/companies" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> All companies
      </Link>

      <div className="card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-800">Company details</h3>
          <div className="flex items-center gap-3">
            <a
              href={`/?preview=${id}`} target="_blank" rel="noopener"
              className="btn-secondary text-sm"
              title="Open this site's client portal in a new tab"
            >
              <Eye className="w-4 h-4" /> Preview as client
            </a>
            <button onClick={archive} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
              <Trash2 className="w-4 h-4" /> Archive
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Client group (parent organisation)</span>
            <select
              value={form.client_group_id || ''}
              onChange={e => setForm({ ...form, client_group_id: e.target.value || null })}
              className="input-field"
            >
              <option value="">— none —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
          {FIELDS.map(([key, label, type]) => (
            <label key={key} className={type === 'textarea' ? 'sm:col-span-2' : ''}>
              <span className="text-xs text-gray-500 block mb-1">{label}</span>
              {type === 'textarea'
                ? <textarea value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} className="input-field" rows={2} />
                : <input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} className="input-field" />
              }
            </label>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <SavedIndicator savedAt={savedAt} />
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3">Product cards</h3>
        <form onSubmit={addCard} className="flex gap-2 mb-3">
          <input
            type="text"
            value={newCardName}
            onChange={e => setNewCardName(e.target.value)}
            placeholder="New card name (e.g. 'Monthly reorder')"
            className="input-field"
          />
          <button type="submit" className="btn-primary">
            <Plus className="w-4 h-4" /> Add card
          </button>
        </form>
        {cards.length === 0
          ? <p className="text-sm text-gray-500">No cards yet.</p>
          : (
            <ul className="divide-y divide-gray-100">
              {cards.map(c => (
                <li key={c.id}>
                  <Link to={`/cards/${c.id}`} className="flex items-center gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                    <ClipboardList className="w-5 h-5 text-brand" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">{c.name}</div>
                      {c.notes && <div className="text-xs text-gray-500 truncate">{c.notes}</div>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </div>

      {/* Shareable preview links — send a URL to a prospect so they can
          browse this site's ordering portal without needing a login. */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-brand" /> Shareable preview links
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Generate a link a prospect can open with no login. They see the ordering portal for this site
              in read-only preview mode — real branding, real products, real order history, but nothing they
              click actually places an order.
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newLinkLabel}
            onChange={e => setNewLinkLabel(e.target.value)}
            placeholder="Optional label (e.g. 'John Underhill demo')"
            className="input-field flex-1"
            maxLength={80}
          />
          <button onClick={createPreviewLink} disabled={creatingLink} className="btn-primary">
            <Plus className="w-4 h-4" /> {creatingLink ? 'Creating…' : 'New preview link'}
          </button>
        </div>

        {previewLinks.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No preview links yet. Create one above.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {previewLinks.map(pl => {
              const url = `${window.location.origin}/preview/${pl.token}`
              const revoked = !!pl.revoked_at
              const expired = new Date(pl.expires_at) < new Date()
              const active = !revoked && !expired
              return (
                <li key={pl.token} className="py-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">
                        {pl.label || 'Untitled preview'}
                        {revoked && <span className="ml-2 text-xs text-red-600 font-normal">· revoked</span>}
                        {!revoked && expired && <span className="ml-2 text-xs text-amber-600 font-normal">· expired</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate font-mono">{url}</div>
                      <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Expires {new Date(pl.expires_at).toLocaleDateString()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {pl.view_count} view{pl.view_count === 1 ? '' : 's'}
                          {pl.last_viewed_at && ` · last ${new Date(pl.last_viewed_at).toLocaleString()}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {active && (
                        <button
                          onClick={() => copyPreviewUrl(pl.token)}
                          className="text-xs px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                        >
                          {copiedToken === pl.token
                            ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied</>
                            : <><Copy className="w-3.5 h-3.5" /> Copy URL</>
                          }
                        </button>
                      )}
                      {active && (
                        <a
                          href={url} target="_blank" rel="noopener"
                          className="text-xs px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" /> Open
                        </a>
                      )}
                      {!revoked && (
                        <button
                          onClick={() => revokePreviewLink(pl.token)}
                          className="text-xs px-2 py-1 rounded-md text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
